import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SbkTransfersStorageService } from '../custom-views/sbk-transfer/data-access/services/sbk-transfers-interceptor.storage.service';

/**
 * Mutates the body of `POST /contact-manager/.../contacts` "save beneficiary"
 * requests so the shape matches what the SBK backend expects per payment type
 * (Pesalink, RTGS, intra-bank, M-PESA, bill, MOJA).
 *
 */
@Injectable()
export class TransfersSaveBeneficiaryInterceptor implements HttpInterceptor {
  constructor(private sbkTransfersStorageService: SbkTransfersStorageService) { }

  intercept<R, T>(req: HttpRequest<R>, next: HttpHandler): Observable<HttpEvent<T>> {
    const paymentData: any = this.sbkTransfersStorageService.paymentDataObj;

    if (!this.isPostRequest(req) || !this.isSavingContact(req, paymentData)) {
      return next.handle(req);
    }

    if (paymentData.paymentType === 'SBK_PESALINK_PAYMENT') {
      const modifiedBody = {
        id: "",
        name: paymentData.counterparty.beneficiaryName,
        accounts: [
          {
            accountNumber: paymentData.counterparty.accountNumber,
            bankName: paymentData.counterparty.bankName,
            accountHolderAddressLine1: paymentData.counterparty.beneficiaryAddress,
            additions: {
              beneficiaryType: "pesalink",
              bankBranchName: paymentData.counterparty.bankName,
              sortCode: paymentData.counterparty.bankBranchCode,
              bankName: paymentData.counterparty.bankName
            },
            name: paymentData.counterparty.name,
            bankCode: paymentData.counterparty.bankBranchCode
          }
        ]
      };

      const modifiedReq = req.clone({ body: modifiedBody });

      return next.handle(modifiedReq);
    }

    if (paymentData.paymentType === 'SBK_RTGS_PAYMENT') {
      const modifiedBody = {
        id: "",
        name: paymentData.counterparty.name,
        accounts: [
          {
            accountNumber: paymentData.counterparty.accountNumber,
            bankName: paymentData.counterparty.bankName,
            accountHolderAddressLine1: paymentData.counterparty.beneficiaryAddress,
            additions: {
              beneficiaryType: "rtgs",
              bankBranchName: paymentData.counterparty.bankName,
              swiftCode: paymentData.counterparty.bankBranchCode,
              bankName: paymentData.counterparty.bankName,
              beneficiaryStreet: paymentData.counterparty.beneficiaryStreet,
              beneficiaryCntryTown: paymentData.counterparty.beneficiaryCntryTown,
            },
            name: paymentData.counterparty.name,
            bankCode: paymentData.counterparty.bankBranchCode
          }
        ]
      };

      const modifiedReq = req.clone({ body: modifiedBody });

      return next.handle(modifiedReq);
    }

    if (paymentData.paymentType === 'SBK_INTRA_PAY') {
      const modifiedBody = {
        id: "",
        name: paymentData.counterparty.name,
        accounts: [
          {
            accountNumber: paymentData.counterparty.accountNumber,
            additions: {
              beneficiaryType: "sbk",
              bankName: 'CFC Stanbic Bank Limited'
            },
            name: paymentData.counterparty.name
          }
        ]
      };

      const modifiedReq = req.clone({ body: modifiedBody });

      return next.handle(modifiedReq);
    }

    if (paymentData.paymentType === 'SBK_MPESA') {
      const counterparty = paymentData.counterparty;
      const additions: any = {
        beneficiaryType: 'mobilePhone'
      };

      if (counterparty.counterPartyType === 'buyGoods') {
        additions.tillNumber = counterparty.tillNumber;
        additions.beneficiaryType = 'mobileTillNumber';
      } else if (counterparty.counterPartyType === 'payBill') {
        additions.paybillNumber = counterparty.paybillNumber;
        additions.beneficiaryType = 'mobilePayBill';
      }

      const mobileAccountNumber =
        counterparty.counterPartyType === 'mobilePhone'
          ? normalizeKenyanMobileNumber(counterparty.accountNumber)
          : counterparty.accountNumber;

      const modifiedBody = {
        id: "",
        name: counterparty.name,
        accounts: [
          {
            accountNumber: counterparty.counterPartyType === 'buyGoods' ? counterparty.tillNumber : mobileAccountNumber,
            additions,
            name: counterparty.name
          }
        ]
      };

      const modifiedReq = req.clone({ body: modifiedBody });
      return next.handle(modifiedReq);
    }


    if (paymentData.paymentType === 'SBK_BILL_PAYMENT') {
      const billerName = paymentData.counterparty.name;

      const modifiedBody = {
        id: "",
        name: billerName,
        accounts: [
          {
            accountNumber: paymentData.counterparty.accountNumber,
            additions: {
              beneficiaryType: "biller",
              billerName: billerName
            },
          }
        ]
      };

      const modifiedReq = req.clone({ body: modifiedBody });
      return next.handle(modifiedReq);
    }

    if (paymentData.paymentType === 'SBK_MOJA_PAYMENT') {
      const billerName = paymentData.counterparty.name;
      const topUpType = paymentData.counterparty.topUpType;
      const obuCardNumber = paymentData.counterparty.obuCardNumber;
      const mtcCardNumber = paymentData.counterparty.mtcCardNumber;
      const mojaIdNumber = paymentData.counterparty.idNumber;
      const plateNumber = paymentData.counterparty.plateNumber;
      const userType = paymentData.counterparty.userType;

      const modifiedBody = {
        id: "",
        name: billerName,
        accounts: [
          {
            accountNumber: topUpType === 'obu' ? obuCardNumber : topUpType === 'plateNumber' ? plateNumber : mtcCardNumber,
            additions: {
              beneficiaryType: "biller",
              billerName: 'Nairobi ExpressWay',
              idKraNumber: mojaIdNumber,
              chargeType: userType,
              cardEtcType: topUpType === 'obu' ? 'ETC.NUMBER' : topUpType === 'plateNumber' ? 'ETC.VEHICLE.NO' : 'CARD.NUMBER'
            },
          }
        ]
      };

      const modifiedReq = req.clone({ body: modifiedBody });
      return next.handle(modifiedReq);
    }

    return next.handle(req);
  }

  isPostRequest(req: HttpRequest<any>): boolean {
    return req.method.toUpperCase() === 'POST';
  }

  isSavingContact(req: HttpRequest<any>, paymentData: any): boolean {
    return req.url.endsWith('/api/contact-manager/client-api/v2/contacts') && !!paymentData;
  }
}

function normalizeKenyanMobileNumber(value: string | null | undefined): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('254')) return digits;
  if (digits.startsWith('0')) return `254${digits.slice(1)}`;
  if (digits.length === 9) return `254${digits}`;
  return digits;
}
